import React, { useState } from 'react';
import { useStore } from '../../store/useStore';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, MessageSquare, CheckCircle, Clock, CheckSquare, Edit2, Save, X } from 'lucide-react';
import { format } from 'date-fns';
import { Task, TaskComment } from '../../types/task';
import { supabase } from '../../config/supabase';
import { useTaskActions } from '../../hooks/tasks/useTaskActions';
import toast from 'react-hot-toast';

interface TaskCardProps {
  task: Task;
  showDepartment?: boolean;
}

export const TaskCard: React.FC<TaskCardProps> = ({ task, showDepartment = false }) => {
  const { currentUser, addTaskComment, availableUsers } = useStore();
  const { acknowledgeTask, updateTaskStatus } = useTaskActions();
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAcknowledging, setIsAcknowledging] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(task.title);
  const [editedDescription, setEditedDescription] = useState(task.description);

  const comments = task.comments || [];
  const subtasks = task.subtasks || [];

  // Helper function to get user name from ID
  const getUserName = (userId: string) => {
    const user = availableUsers.find(u => u.id === userId);
    return user?.name || 'Unknown User';
  };

  const handleAcknowledge = async () => {
    if (!currentUser) return;
    
    try {
      setIsAcknowledging(true);
      await acknowledgeTask(task.id);
    } catch (error) {
      console.error('Failed to acknowledge task:', error);
      toast.error('Failed to acknowledge task');
    } finally {
      setIsAcknowledging(false);
    }
  };

  const handleComplete = async () => {
    if (!currentUser) return;
    
    try {
      setIsCompleting(true);
      await updateTaskStatus(task.id, 'COMPLETED');
    } catch (error) {
      console.error('Failed to complete task:', error);
      toast.error('Failed to complete task');
    } finally {
      setIsCompleting(false);
    }
  };

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !newComment.trim()) return;
    
    setIsSubmitting(true);
    try {
      // Insert comment into Supabase
      const { data: comment, error } = await supabase
        .from('task_comments')
        .insert({
          task_id: task.id,
          user_id: currentUser.id,
          content: newComment.trim(),
          created_at: new Date().toISOString()
        })
        .select(`
          *,
          user:profiles!task_comments_user_id_fkey (
            id,
            name,
            avatar,
            department,
            role
          )
        `)
        .single();

      if (error) throw error;

      // Map the comment to include user information
      const mappedComment: TaskComment = {
        id: comment.id,
        taskId: comment.task_id,
        userId: comment.user_id,
        content: comment.content,
        createdAt: new Date(comment.created_at),
        userName: comment.user?.name || currentUser.name,
        userAvatar: comment.user?.avatar || currentUser.avatar
      };

      // Add comment to local store
      addTaskComment(task.id, mappedComment);
      setNewComment('');
      toast.success('Comment added');
    } catch (error) {
      console.error('Failed to add comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editedTitle.trim()) {
      toast.error('Title cannot be empty');
      return;
    }

    try {
      // Update task in Supabase
      const { error } = await supabase
        .from('tasks')
        .update({
          title: editedTitle,
          description: editedDescription,
          updated_at: new Date().toISOString()
        })
        .eq('id', task.id);

      if (error) throw error;

      // Update local store
      useStore.getState().updateTask(task.id, {
        ...task,
        title: editedTitle,
        description: editedDescription,
        updatedAt: new Date()
      });

      setIsEditing(false);
      toast.success('Task updated successfully');
    } catch (error) {
      console.error('Failed to update task:', error);
      toast.error('Failed to update task');
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
      <div className="space-y-4">
        {/* Task Header */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            {isEditing ? (
              <div className="space-y-2">
                <input
                  type="text"
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-emerald-500"
                  placeholder="Task title"
                />
                <textarea
                  value={editedDescription}
                  onChange={(e) => setEditedDescription(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md h-20 focus:ring-2 focus:ring-emerald-500"
                  placeholder="Task description"
                />
              </div>
            ) : (
              <>
                <h3 className="font-medium text-gray-900 break-words">{task.title}</h3>
                <p className="text-sm text-gray-600 mt-1 break-words">{task.description}</p>
              </>
            )}
            {showDepartment && (
              <div className="mt-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {task.department}
                </span>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center gap-2">
            {isEditing ? (
              <>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSaveEdit}
                  icon={<Save className="w-4 h-4" />}
                >
                  Save
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setIsEditing(false);
                    setEditedTitle(task.title);
                    setEditedDescription(task.description);
                  }}
                  icon={<X className="w-4 h-4" />}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsEditing(true)}
                icon={<Edit2 className="w-4 h-4" />}
                className="text-gray-600 hover:text-gray-900"
              />
            )}
            {currentUser?.id === task.assignedTo && (
              <div className="flex flex-wrap gap-2">
                {!task.acknowledged && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAcknowledge}
                    loading={isAcknowledging}
                    icon={<Clock className="w-4 h-4" />}
                    className="text-blue-600 border-blue-200 hover:bg-blue-50 whitespace-nowrap"
                  >
                    <span className="hidden sm:inline">Acknowledge</span>
                  </Button>
                )}
                {task.acknowledged && task.status !== 'COMPLETED' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleComplete}
                    loading={isCompleting}
                    icon={<CheckSquare className="w-4 h-4" />}
                    className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 whitespace-nowrap"
                  >
                    <span className="hidden sm:inline">Complete</span>
                  </Button>
                )}
              </div>
            )}
            {task.status === 'COMPLETED' && (
              <div className="flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm whitespace-nowrap">
                <CheckSquare className="w-4 h-4" />
                <span className="text-xs font-medium">Completed</span>
              </div>
            )}
            {task.acknowledged && task.status !== 'COMPLETED' && (
              <div className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-sm whitespace-nowrap">
                <CheckCircle className="w-4 h-4" />
                <span className="text-xs font-medium">Acknowledged</span>
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowComments(!showComments)}
              icon={<MessageSquare className="w-4 h-4" />}
              className="relative"
            >
              {comments.length > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 text-white text-xs rounded-full flex items-center justify-center">
                  {comments.length}
                </span>
              )}
            </Button>
          </div>
        </div>

        {/* Task Metadata */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
          <div className="flex items-center gap-1.5">
            <span>From: {getUserName(task.assignedBy)}</span>
            <span className="hidden sm:inline">•</span>
            <span>Due {format(task.dueDate ? new Date(task.dueDate) : new Date(), 'MMM d, h:mm a')}</span>
          </div>
        </div>
      </div>
      
      {/* Subtasks Section */}
      {subtasks.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Subtasks</h4>
          <div className="space-y-3">
            {subtasks.map((subtask) => (
              <div
                key={subtask.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-2 min-w-0">
                  {subtask.status === 'COMPLETED' ? (
                    <CheckSquare className="w-4 h-4 shrink-0 text-emerald-500" />
                  ) : subtask.acknowledged ? (
                    <CheckCircle className="w-4 h-4 shrink-0 text-blue-500" />
                  ) : (
                    <Clock className="w-4 h-4 shrink-0 text-gray-400" />
                  )}
                  <span className="text-sm text-gray-700 truncate">{subtask.title}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`ml-2 text-xs font-medium px-2 py-1 rounded-full ${
                    subtask.status === 'COMPLETED'
                      ? 'bg-emerald-100 text-emerald-700'
                      : subtask.acknowledged
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {subtask.status === 'COMPLETED' 
                      ? 'Completed' 
                      : subtask.acknowledged 
                      ? 'In Progress'
                      : 'Pending'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Comments Section */}
      <motion.div
        initial={false}
        animate={{ height: showComments ? 'auto' : 0, opacity: showComments ? 1 : 0 }}
        className={`overflow-hidden ${showComments ? 'mt-4 pt-4 border-t border-gray-200' : ''}`}
      >
        <div className="space-y-4">
          {comments.map((comment) => (
            <motion.div
              key={comment.id}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-start gap-3"
            >
              <img
                src={comment.userAvatar}
                alt={comment.userName}
                className="w-8 h-8 rounded-full shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-900 truncate">{comment.userName}</span>
                  <span className="text-xs text-gray-500 shrink-0">
                    {format(new Date(comment.createdAt), 'MMM d, h:mm a')}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-1 break-words">{comment.content}</p>
              </div>
            </motion.div>
          ))}

          {/* Comment Form */}
          <form onSubmit={handleAddComment} className="mt-4">
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment..."
                className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
              <Button
                type="submit"
                variant="primary"
                size="sm"
                icon={<Send className="w-4 h-4" />}
                disabled={!newComment.trim() || isSubmitting}
                loading={isSubmitting}
                className="whitespace-nowrap"
              >
                <span className="hidden sm:inline">Send</span>
              </Button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
};